import React, { useEffect, useState, useRef, useCallback } from "react";
import { DeviceEmulator, DeviceFrameset } from "react-device-frameset";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import InlineTooltip from "../UI/Tooltip/InlineTooltip";
import Accordion from "./FieldGroupAccordion";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Toggle from "../UI/Toggle";
// import { useUserContext } from "../../contexts/UserContext";
// import { hasUploadFields } from "../../utils/uploadHelper";
// import { getContentfulAsset } from "../../utils/APIs";
import HtmlViewerWithInspector from "../Template/HtmlViewerWithInspector";
import { useUserContext } from "../../contexts/UserContext";
import { hasUploadFields } from "../../utils/uploadHelper";
import { getContentfulAsset } from "../../utils/APIs";

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const ConfigureExperience = ({
  selectedTemplate,
  fieldValues,
  setFieldValues,
  transformedExperienceJSON,
  setTransformedExperienceJSON,
  actionText,
  dynamicFields,
  setDynamicFields,
  isCreateExperience = false,
  isEditEnable = true,
  validationError = "",
  pendingUploads,
  setPendingUploads,
}) => {
  const { enums, content } = useContentfulContext();
  const { userData } = useUserContext();
  const previewIframeRef = useRef(null);
  const [, setEnableButton] = useState(false);

  const hasContentfulUpload = selectedTemplate?.config_json
    ? hasUploadFields(selectedTemplate.config_json, 'contentful')
    : false;

  const [assetUrls, setAssetUrls] = useState({});

  useEffect(() => {
    const fetchAssetUrls = async () => {
      if (!hasContentfulUpload || !fieldValues) return;
      const configItems = JSON.parse(selectedTemplate.config_json);
      const newUrls = { ...assetUrls };
      let updated = false;

      for (const item of configItems) {
        if (item.input?.input_type === 'upload' && item.input?.platform === 'contentful') {
          const fieldId = item.exp_json_field || item.id;
          const val = fieldValues[fieldId];
          let assetId = null;

          if (val && typeof val === 'object' && val.sys?.id) {
            assetId = val.sys.id;
          } else if (typeof val === 'string' && val.trim().length > 0 && !val.startsWith('blob:') && !val.startsWith('http') && !val.startsWith('//')) {
            assetId = val;
          }

          if (assetId) {
            if (!newUrls[fieldId]) {
              try {
                const assetData = await getContentfulAsset(userData?.token, userData?.selectedOrganization?.org_token, assetId, userData?.defaultContentful?._id);
                const url = assetData?.fields?.file?.url;
                if (url) {
                  // Contentful URLs usually start with //, ensure they have https:
                  newUrls[fieldId] = url.startsWith('//') ? `https:${url}` : url;
                  updated = true;
                }
              } catch (e) {
                console.error("Failed to fetch Contentful asset", e);
              }
            }
          }
        }
      }

      if (updated) {
        setAssetUrls(newUrls);
      }
    };
    fetchAssetUrls();
  }, [fieldValues, selectedTemplate, userData?.token]);

  const handleFileSelected = (fieldId, file) => {
    if (typeof setPendingUploads === 'function') {
      setPendingUploads((prev) => {
        const next = new Map(prev);
        next.set(fieldId, file);
        return next;
      });
    }
  };

  const getBaseJSON = () => {
    const base_json = {};
    JSON.parse(selectedTemplate.config_json).forEach(function (item, index) {
      base_json[item.id] = item.input.input_default_value;
    });
    if (fieldValues) {
      return fieldValues;
    }
    return base_json;
  };

  const [baseJSON, setBaseJSON] = useState(getBaseJSON);
  const [previewExperienceJSON, setPreviewExperienceJSON] = useState(null);
  const [openIndices, setOpenIndices] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const accordionRef = useRef();
  const viewBackgroundImageRef = useRef(true);
  const landscapeRef = useRef(false);

  const [hasGroups, setHasGroups] = useState();

  const handleToggleClick = () => {
    if (accordionRef.current) {
      accordionRef.current.toggleExpandAll();
    }
  };

  const getViewUrl = (isLandscape, urls) => {
    if (isLandscape) {
      return urls?.landscape_only || ""
    } else {
      return urls?.portrait_only || ""
    }
  };

  const [configElements, setConfigElements] = useState(() => {
    const config_elems = [];
    JSON.parse(selectedTemplate.config_json).forEach((el) => {
      el.error = "";
      config_elems.push(el);
    });
    return config_elems;
  });

  const handleInputChanges = (e) => {
    const { name, value } = e.target;
    setFieldValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeDynamicField = (e) => {
    setDynamicFields((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  function transformExperienceJSON(overrideValues = fieldValues) {
    const transformedJSON = {};
    for (const key in overrideValues) {
      const keys = key.split(".");
      keys.reduce((acc, part, index) => {
        if (part.includes("[")) {
          const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
          if (!acc[arrayKey]) {
            acc[arrayKey] = [];
          }
          if (!acc[arrayKey][arrayIndex]) {
            acc[arrayKey][arrayIndex] = {};
          }
          if (index === keys.length - 1) {
            acc[arrayKey][arrayIndex] = overrideValues[key];
          }
          return acc[arrayKey][arrayIndex];
        } else {
          if (index === keys.length - 1) {
            acc[part] = overrideValues[key];
          } else {
            if (!acc[part]) {
              acc[part] = {};
            }
            return acc[part];
          }
        }
      }, transformedJSON);
    }
    return transformedJSON;
  }

  // Get experience JSON injected with asset URLs for iframe preview
  const getPreviewExperienceJSON = () => {
    const previewFieldValues = { ...fieldValues };
    if (hasContentfulUpload && selectedTemplate?.config_json) {
      const configItems = JSON.parse(selectedTemplate.config_json);
      configItems.forEach((item) => {
        if (item.input?.input_type === 'upload' && item.input?.platform === 'contentful') {
          const fieldId = item.exp_json_field || item.id;
          const rawVal = fieldValues[fieldId];
          const isBlob = typeof rawVal === 'string' && rawVal.startsWith('blob:');

          if (!isBlob && assetUrls[fieldId]) {
            previewFieldValues[fieldId] = assetUrls[fieldId];
          }
        }
      });
    }
    return transformExperienceJSON(previewFieldValues);
  };

  useEffect(() => {
    setTransformedExperienceJSON(transformExperienceJSON());
    setPreviewExperienceJSON(getPreviewExperienceJSON());
  }, []);

  const handleSaveChanges = () => {
    const newConfigElems = [...configElements];
    let isValid = true;
    for (let item of newConfigElems) {
      const val = fieldValues[item.id];
      const rules = item.rules;
      if (rules.is_mandatory) {
        if (val?.length == 0) {
          item.error = "Field is required";
          isValid = false;
        } else {
          item.error = "";
        }
      }
      if (rules.min_length && rules.max_length && item.error.length == 0) {
        if (val?.length > rules.max_length || val?.length < rules.min_length) {
          if (item.input.input_type != "array") {
            item.error = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
            isValid = false;
          }
        } else {
          item.error = "";
        }
      }
    }

    setTransformedExperienceJSON(transformExperienceJSON());
    setPreviewExperienceJSON(getPreviewExperienceJSON());

    if (!isValid) {
      setConfigElements(newConfigElems);
      return;
    }

    setBaseJSON(fieldValues);

  }
  const saveChangesRef = useRef(handleSaveChanges);
  useEffect(() => {
    saveChangesRef.current = handleSaveChanges;
  }, [handleSaveChanges]);

  const debouncedSaveChanges = useCallback(
    debounce(() => {
      if (saveChangesRef.current) saveChangesRef.current();
    }, 400),
    []
  );

  // Update preview live on field value change or when asset URLs are fetched
  useEffect(() => {
    if (isEditEnable) {
      debouncedSaveChanges();
    }
  }, [fieldValues, assetUrls, configElements, debouncedSaveChanges]);

  useEffect(() => {
    if (validationError) {
      debouncedSaveChanges();
    }
  }, [validationError, debouncedSaveChanges]);

  useEffect(() => {
    if (JSON.stringify(baseJSON) !== JSON.stringify(fieldValues)) {
      setEnableButton(false);
    } else {
      setEnableButton(true);
    }
  }, [baseJSON, fieldValues]);

  // Preview updates are now handled reactively by passing getPreviewExperienceJSON() directly to HtmlViewerWithInspector
  const getViewBackgroundUrl = () => {
    return viewBackgroundImageRef.current && getViewUrl(landscapeRef.current, selectedTemplate?.view_url)
      ? getViewUrl(landscapeRef.current, selectedTemplate?.view_url)
      : "";
  };

  return (
    <div className="!h-full">
      <div className={`${isEditEnable && "flex flex-col flex-grow xs:flex-row"} space-x-1 h-full`}>
        {isEditEnable && (
          <aside className="w-full md:w-1/4 relative rounded-xl bg-white border border-border shadow-card overflow-hidden h-full flex flex-col">
            <div className="p-3.5 border-b border-border bg-surface-alt/50 space-y-2 shrink-0">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-stride-muted">{actionText}</p>
                {hasGroups && (
                  <Toggle
                    id="configure-experience-expand-toggle"
                    checked={expandAll}
                    onChange={handleToggleClick}
                    label={expandAll ? 'Collapse' : 'Expand'}
                    size="sm"
                  />
                )}
              </div>
              <h1 className="text-xs font-medium text-stride-body flex items-center flex-wrap gap-1">
                <span className="text-stride-muted">{content.CONFIGURE_EXPERIENCE_CONTENT.template_key}:</span>
                <span className="font-bold text-stride-heading">{selectedTemplate.name}</span>
                {isCreateExperience && (selectedTemplate?.source == enums?.templateSources.api.id)
                  && <InlineTooltip content={content.CONFIGURE_EXPERIENCE_CONTENT.template_tooltip_title} imgClass={"pt-0.5"} />}
              </h1>
            </div>
            <div id="configure-experience-form-id" className="flex-1 overflow-x-hidden overflow-y-auto p-3 space-y-4">
              <div className="w-full max-w-2xl mx-auto">
                <Accordion configElements={configElements} fieldValues={fieldValues} assetUrls={assetUrls} handleInputChanges={handleInputChanges} dynamicFields={dynamicFields} handleChangeDynamicField={handleChangeDynamicField} openIndices={openIndices} setOpenIndices={setOpenIndices} expandAll={expandAll} setExpandAll={setExpandAll} accordionRef={accordionRef} hasGroups={hasGroups} setHasGroups={setHasGroups} onFileSelected={handleFileSelected} />
              </div>
            </div>
          </aside>
        )}
        <div className={`${isEditEnable && "w-full md:w-3/4 max-w-screen"} z-20 px-2 md:px-8 rounded max-h-[calc(100vh-80px)] h-full overflow-y-auto scrollbar-width-2px`}>
          <div className="editor rounded border-gray-3 border-x border-b w-full h-full min-h-[calc(100vh-160px)]">
            {selectedTemplate.type == enums?.templateTypes.no_preview.id ? (
              <>
                <CodeMirror
                  value={JSON.stringify(transformedExperienceJSON, null, 2)?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>')}
                  readOnly={true}
                  editable={false}
                  height="100%"
                  width="100%"
                  lineWrapping={true}
                  extensions={[EditorView.lineWrapping]}
                />
              </>
            ) : (
              <DeviceEmulator
                defaultDevice={selectedTemplate?.default_device}
                isLandscapeSupport={selectedTemplate?.landscape_support}
                platformType={selectedTemplate?.platform}
                onChange={(event) => {
                  const isLandscape = !!event?.landscape;
                  const showBackground = !!event?.viewBackgroundImage;
                  if (landscapeRef.current !== isLandscape || viewBackgroundImageRef.current !== showBackground) {
                    landscapeRef.current = isLandscape;
                    viewBackgroundImageRef.current = showBackground;
                    handleSaveChanges();
                  }
                }}
              >
                {(props) => (
                  <DeviceFrameset {...props}>
                    <HtmlViewerWithInspector
                      htmlCode={selectedTemplate?.html || ""}
                      cssCode={selectedTemplate?.css || ""}
                      experience_json={previewExperienceJSON || getPreviewExperienceJSON()}
                      configJsonData={[]}
                      configFieldNameMain={null}
                      inspectorEnabled={false}
                      onElementUpdate={() => { }}
                      web_url={getViewBackgroundUrl()}
                      templateType={selectedTemplate?.type}
                    />
                  </DeviceFrameset>
                )}
              </DeviceEmulator>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureExperience;
